import { useForm } from 'react-hook-form';
import { BillingApi, BillingInfoDto } from '../../billing.api';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/components/ui/cn';
import MaskInput from '@/components/inputs/MaskInput';
import { InputController } from '@/components/inputs/InputController';
import DropdownInput from '@/components/inputs/DropdownInput';
import TextInput from '@/components/inputs/TextInput';

export enum BillingFormLayoutType {
	Onboarding,
	BillingView,
	BillingChange,
}

type Props = {
	formName: string;
	className?: string;
	initialData?: Partial<BillingInfoDto>;
	layoutType: BillingFormLayoutType;
	onSubmit: (data: BillingInfoDto) => void | Promise<void>;
	onDirtyChange?: (isDirty: boolean) => void;
};

export type BillingInfoFormType = BillingInfoDto & {
	Country: string;
}

export type BillingInfoFormRef = {
	submit: () => void;
	reset: (data?: Partial<BillingInfoDto>) => void;
	getValues: () => BillingInfoFormType;
};

const BillingInfoForm = forwardRef<BillingInfoFormRef, Props>(({
	formName,
	className,
	initialData,
	onSubmit,
	layoutType,
	...props
}, ref) => {
	const [tinKey, setTinKey] = useState(0);

	const defaultValues = {
		LegalName: '',
		Address1: '',
		Address2: '',
		Province: undefined,
		City: undefined,
		PostalCode: '',
		Country: 'Philippines',
		Tin: '',
	};
	const formId = formName;
	const form = useForm<BillingInfoFormType>({
		defaultValues: defaultValues,
		resolver: yupResolver(yup.object({
			LegalName: yup.string().trim().required(VALIDATION_MESSAGES.required),
			Address1: yup.string().trim().required(VALIDATION_MESSAGES.required),
			Address2: yup.string().optional(),
			Province: yup.object()
				.shape({ Id: yup.number().required(), Name: yup.string().required() })
				.required(VALIDATION_MESSAGES.required),
			City: yup.object()
				.shape({ Id: yup.number().required(), Name: yup.string().required() })
				.required(VALIDATION_MESSAGES.required),
			PostalCode: yup.string().trim()
				.required(VALIDATION_MESSAGES.required)
				.matches(/^\d{4}$/, 'Postal code must be 4 digits'),
			Country: yup.string().required(),
			Tin: yup.string().trim()
				.test('tin-format', 'TIN must be in format XXX-XXX-XXX-XXX', (value) => {
					if (!value || value === '') return true;
					return /^\d{3}-\d{3}-\d{3}-\d{3}$/.test(value);
				})
				.optional()
		})),
		mode: 'onBlur',
	});

	useEffect(() => {
		if (initialData) {
			form.reset({
				LegalName: initialData.LegalName || '',
				Address1: initialData.Address1 || '',
				Address2: initialData.Address2 || '',
				Province: initialData.Province || undefined,
				City: initialData.City || undefined,
				PostalCode: initialData.PostalCode || '',
				Country: "Philippines",
				Tin: initialData.Tin || '',
			});

			setTinKey(prev => prev + 1);
		}
	}, [initialData]);

	useEffect(() => {
		props.onDirtyChange?.(form.formState.isDirty);
	}, [form.formState.isDirty, props.onDirtyChange]);

	useImperativeHandle(ref, () => ({
		submit: form.handleSubmit(onSubmit),
		reset: (data?: Partial<BillingInfoDto>) => data ? form.reset({
			LegalName: data.LegalName || '',
			Address1: data.Address1 || '',
			Address2: data.Address2 || '',
			Province: data.Province || undefined,
			City: data.City || undefined,
			PostalCode: data.PostalCode || '',
			Country: "Philippines",
			Tin: data.Tin || '',
		}) : form.reset(defaultValues),
		getValues: () => form.getValues(),
	}));

	const selectedProvince = form.watch('Province');

	const provincesQuery = useQuery({
		queryKey: ['BillingInfoForm.provinces'],
		queryFn: BillingApi.getProvinces,
		staleTime: 5 * 60 * 1000,
	});

	const citiesQuery = useQuery({
		queryKey: ['BillingInfoForm.cities', selectedProvince?.Id],
		queryFn: () => BillingApi.getCities(selectedProvince.Id),
		enabled: !!selectedProvince,
		staleTime: 5 * 60 * 1000,
	});

	useEffect(() => {
		if (!selectedProvince) form.setValue('City', undefined as any)
		else {
			const currentCity = form.getValues('City');
			if (currentCity && citiesQuery.data) {
				const cityExists = citiesQuery.data.some(city => city.Id === currentCity.Id);
				if (!cityExists) form.setValue('City', undefined as any);
			}
		}
	}, [selectedProvince?.Id, citiesQuery.data]);

	const extraLabel = layoutType === BillingFormLayoutType.Onboarding ? '*' : '';
	const disabled = layoutType === BillingFormLayoutType.BillingView;

	const legalNameInput = (
		<InputController id="LegalName" control={form.control}>
			<TextInput label={`Legal Name${extraLabel}`} disabled={disabled} />
		</InputController>
	);
	const address1NameInput = (
		<InputController id="Address1" control={form.control}>
			<TextInput label={`Address Line 1${extraLabel}`} disabled={disabled} />
		</InputController>
	);
	const address2NameInput = (
		<InputController id="Address2" control={form.control}>
			<TextInput label="Address Line 2" disabled={disabled} />
		</InputController>
	);
	const provinceInput = (
		<InputController id="Province" control={form.control}>
			<DropdownInput
				disabled={disabled || provincesQuery.isLoading}
				label={`Province${extraLabel}`}
				optionLabel="Name"
				options={provincesQuery.data || []}
			/>
		</InputController>
	);
	const cityInput = (
		<InputController id="City" control={form.control}>
			<DropdownInput
				key={citiesQuery.data?.map(city => city.Id).join(',')}
				disabled={disabled || !selectedProvince || citiesQuery.isLoading}
				label={`City${extraLabel}`}
				optionLabel="Name"
				options={citiesQuery.data || []}
			/>
		</InputController>
	);
	const postalCodeInput = (
		<InputController id="PostalCode" control={form.control}>
			<TextInput label={`Postal Code${extraLabel}`} disabled={disabled} />
		</InputController>
	);
	const countryInput = (
		<InputController id="Country" control={form.control}>
			<DropdownInput
				disabled
				options={["Philippines"]}
				label={`Country${extraLabel}`}
			/>
		</InputController>
	);
	const tinInput = (
		<InputController id="Tin" control={form.control}>
			<MaskInput
				key={tinKey}
				label="TIN"
				disableAutoSelect
				autoComplete="new-password"
				mask="999-999-999-999"
				autoClear={false}
				disabled={disabled}
			/>
		</InputController>
	);

	return (
		<>
			{layoutType === BillingFormLayoutType.Onboarding && (
				<form
					id={formId}
					onSubmit={form.handleSubmit(onSubmit)}
					className={cn(className, 'p-input-filled', 'flex flex-col gap-8')}
				>
					{legalNameInput}

					<div className="flex flex-col gap-6">
						{address1NameInput}
						{address2NameInput}
					</div>

					<div className="flex flex-col md:flex-row gap-6">
						{provinceInput}
						{cityInput}
					</div>

					{postalCodeInput}
					{countryInput}
					{tinInput}
				</form>
			)}
			{layoutType === BillingFormLayoutType.BillingChange && (
				<form
					id={formId}
					onSubmit={form.handleSubmit(onSubmit)}
					className={cn(className, 'p-input-filled', 'flex flex-col gap-4')}
				>
					{legalNameInput}

					<div className="flex flex-col gap-3">
						{address1NameInput}
						{address2NameInput}
					</div>

					<div className="flex flex-col md:flex-row gap-3">
						{provinceInput}
						{cityInput}
					</div>

					{postalCodeInput}
					{countryInput}
					{tinInput}
				</form>
			)}
			{layoutType === BillingFormLayoutType.BillingView && (
				<form
					id={formId}
					onSubmit={form.handleSubmit(onSubmit)}
					className={cn(className, 'p-input-filled w-full md:w-1/2', 'flex flex-col gap-6')}
				>
					{legalNameInput}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-3">
						{address1NameInput}
						{address2NameInput}
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-3">
						{provinceInput}
						{cityInput}
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-3">
						{postalCodeInput}
						{countryInput}
					</div>
					{tinInput}
				</form>
			)}
		</>
	);
});

export default BillingInfoForm;
